<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button class="btn btn-secondary" @click="handleBack">
        <i class="ri-arrow-left-line"></i>
        返回
      </button>
    </div>

    <div class="page-toolbar">
      <div class="filter-group">
        <select v-model="filters.syncType" class="form-control" @change="handleFilterChange">
          <option value="">全部類型</option>
          <option value="member">會員</option>
          <option value="order">訂單</option>
          <option value="points">點數</option>
          <option value="ticket">票券</option>
        </select>

        <select v-model="filters.syncStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="success">成功</option>
          <option value="failed">失敗</option>
          <option value="conflict">衝突</option>
        </select>

        <select v-model="filters.syncDirection" class="form-control" @change="handleFilterChange">
          <option value="">全部方向</option>
          <option value="wixtar_to_crm">Wixtar → CRM</option>
          <option value="crm_to_wixtar">CRM → Wixtar</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchLogs">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>時間</th>
            <th>類型</th>
            <th>方向</th>
            <th>狀態</th>
            <th>實體 ID</th>
            <th>錯誤訊息</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="logs.length === 0">
            <td colspan="6" class="text-center text-muted">尚無同步記錄</td>
          </tr>
          <tr v-for="log in logs" :key="log.id">
            <td>{{ formatDate(log.createdAt) }}</td>
            <td>{{ getSyncTypeName(log.syncType) }}</td>
            <td>{{ getSyncDirectionName(log.syncDirection) }}</td>
            <td>
              <span :class="getStatusBadgeClass(log.syncStatus)">
                {{ getStatusName(log.syncStatus) }}
              </span>
            </td>
            <td>{{ log.entityId || '-' }}</td>
            <td>
              <span v-if="log.errorMessage" class="error-text" :title="log.errorMessage">
                {{ truncate(log.errorMessage, 50) }}
              </span>
              <span v-else>-</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination && pagination.totalPages > 1"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total="pagination.totalItems"
      @change="handlePageChange"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'WixtarSyncLogs',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('Wixtar 同步記錄');

    const loading = ref(false);
    const error = ref(null);
    const logs = ref([]);
    const pagination = ref(null);
    const filters = reactive({
      syncType: '',
      syncStatus: '',
      syncDirection: '',
      page: 1,
      pageSize: 20
    });

    const fetchLogs = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await wixtarService.getSyncLogs(filters);
        if (response && response.success && response.data) {
          logs.value = response.data.items || [];
          pagination.value = response.data.pagination || null;
        } else {
          error.value = response?.message || '獲取同步記錄失敗';
        }
      } catch (err) {
        console.error('獲取同步記錄失敗:', err);
        error.value = err.message || '獲取同步記錄失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleFilterChange = () => {
      filters.page = 1;
      fetchLogs();
    };

    const handlePageChange = (page) => {
      filters.page = page;
      fetchLogs();
    };

    const getSyncTypeName = (type) => {
      const types = {
        member: '會員',
        order: '訂單',
        points: '點數',
        ticket: '票券'
      };
      return types[type] || type;
    };

    const getSyncDirectionName = (direction) => {
      const directions = {
        wixtar_to_crm: 'Wixtar → CRM',
        crm_to_wixtar: 'CRM → Wixtar'
      };
      return directions[direction] || direction;
    };

    const getStatusName = (status) => {
      const statuses = {
        success: '成功',
        failed: '失敗',
        conflict: '衝突'
      };
      return statuses[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        success: 'badge badge-success',
        failed: 'badge badge-danger',
        conflict: 'badge badge-warning'
      };
      return classes[status] || 'badge badge-secondary';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    const truncate = (text, length) => {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    const handleBack = () => {
      router.push({ name: 'ClientSystemSettings', query: { tab: 'wixtar' } });
    };

    onMounted(() => {
      fetchLogs();
    });

    return {
      pageTitle,
      loading,
      error,
      logs,
      pagination,
      filters,
      fetchLogs,
      handleFilterChange,
      handlePageChange,
      getSyncTypeName,
      getSyncDirectionName,
      getStatusName,
      getStatusBadgeClass,
      formatDate,
      truncate,
      handleBack
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-wrapper table {
  min-width: 800px;
  width: 100%;
  border-collapse: collapse;
}

.table-wrapper th,
.table-wrapper td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.table-wrapper th {
  background-color: #f8f9fa;
  font-weight: 600;
  font-size: 14px;
}

.table-wrapper td {
  font-size: 14px;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #6c757d;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background-color: #d4edda;
  color: #155724;
}

.badge-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.badge-warning {
  background-color: #fff3cd;
  color: #856404;
}

.badge-secondary {
  background-color: #e2e3e5;
  color: #383d41;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.page-info {
  font-size: 14px;
  color: #6c757d;
}
</style>
